import {
  BATTERY_CHARGE_RATE_PER_MW_PER_TICK,
  HEATER_POWER_MW,
  HEATER_LEVELS,
  LIGHTS_POWER_MW,
  OXYGEN_GENERATOR_POWER_PER_LEVEL_MW,
  POWER_CONTROLLABLE_ROLES,
  ROOM_AUTO_OFF_DEFAULT_OFF_AT,
  ROOM_AUTO_OFF_DEFAULT_ON_AT,
  ROOM_AUTO_OFF_MIN_GAP,
  SIMULATION_INTERVAL_MS
} from '../../shared/constants.js';
import type { GameSnapshot, Role, SubmarineState, ControlInput } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { createCard, createPlusMinusControl } from '../controllerUiHelpers.js';
import { t } from '../i18n.js';

/** Individual power-consuming components within a room, derived from the snapshot. */
const getRoomComponents = (role: Role, sub: SubmarineState): Array<{ label: string; mw: number }> => {
  const total = sub.powerBreakdown?.[role] ?? 0;
  switch (role) {
    case 'life_support':
      return [
        { label: `O₂ generator (${Math.round(sub.oxygenGeneratorLevel ?? 0)}%)`, mw: (sub.oxygenGeneratorLevel ?? 0) * OXYGEN_GENERATOR_POWER_PER_LEVEL_MW },
        { label: 'Cabin heater', mw: sub.heaterActive ? HEATER_POWER_MW * ((sub.heaterLevel ?? HEATER_LEVELS) / HEATER_LEVELS) : 0 }
      ];
    case 'captain':
      return [{ label: 'Forward lights', mw: sub.forwardLightsOn ? LIGHTS_POWER_MW : 0 }];
    case 'docking':
      return [{ label: 'Docking lights', mw: sub.dockingLightsOn ? LIGHTS_POWER_MW : 0 }];
    case 'mining':
      return [{ label: 'Mining lights', mw: sub.miningLightsOn ? LIGHTS_POWER_MW : 0 }];
    case 'periscope':
      return [{ label: 'Periscope light', mw: sub.periscopeLightOn ? 2 * LIGHTS_POWER_MW : 0 }];
    case 'pilot':
      return [{ label: 'Propulsion (thrusters & engines)', mw: total }];
    case 'ballast':
      return [{ label: 'Ballast pumps', mw: total }];
    case 'storage':
      return [{ label: 'Material processor', mw: total }];
    case 'engineer':
      return [{ label: 'Bilge pumps', mw: total }];
    default:
      return total > 0.05 ? [{ label: 'Active draw', mw: total }] : [];
  }
};

type CreateBatteryRoomControllerOptions = {
  controls: HTMLDivElement | null;
  ctrlZoneLeftBottom: HTMLDivElement | null;
  ctrlZoneRightAux: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
};

const ROOM_LABELS: Partial<Record<Role, string>> = {
  navigation: 'Navigation',
  captain: 'Captain',
  docking: 'Docking',
  pilot: 'Pilot',
  ballast: 'Ballast',
  sonar: 'Sonar',
  storage: 'Storage / Processing',
  life_support: 'Life Support',
  drone: 'Drone',
  harpoon: 'Harpoon',
  engineer: 'Engineer',
  periscope: 'Periscope',
  quarters: 'Crew Quarters'
};

type RoomRowRefs = {
  mwValue: HTMLElement;
  expandIcon: HTMLElement;
  body: HTMLElement;
  components: HTMLElement;
  powerButton: HTMLButtonElement;
  autoButton: HTMLButtonElement;
  thresholdWrap: HTMLElement;
  offValue: HTMLElement;
  onValue: HTMLElement;
};

export const createBatteryRoomController = ({
  controls,
  ctrlZoneLeftBottom,
  ctrlZoneRightAux,
  emitControl
}: CreateBatteryRoomControllerOptions) => {
  let batteryRestoreBtn: HTMLButtonElement | null = null;
  let chargeTargetValue: HTMLElement | null = null;
  let runtimeValue: HTMLElement | null = null;
  // Local edit state so rapid +/- taps step from the pending value instead of
  // the (laggier) snapshot round-trip. null = follow the snapshot.
  let pendingChargeTarget: number | null = null;
  const rowRefs = new Map<Role, RoomRowRefs>();
  const rowElements = new Map<Role, HTMLElement>();
  let roomListEl: HTMLElement | null = null;
  const expandedRoles = new Set<Role>();
  // Rolling window of net-MW readings so the runtime estimate doesn't flicker
  // with every transient draw (harpoon shots, sonar pings).
  const netMwWindow: number[] = [];

  // Local edit state for each room's auto-off thresholds (seeded from the snapshot).
  const autoConfig = new Map<Role, { enabled: boolean; offAt: number; onAt: number }>();

  const getAutoConfig = (role: Role): { enabled: boolean; offAt: number; onAt: number } => {
    let config = autoConfig.get(role);
    if (!config) {
      const snapConfig = controllerState.lastSnapshot?.submarine.roomAutoOff?.[role];
      config = snapConfig
        ? { ...snapConfig }
        : { enabled: false, offAt: ROOM_AUTO_OFF_DEFAULT_OFF_AT, onAt: ROOM_AUTO_OFF_DEFAULT_ON_AT };
      autoConfig.set(role, config);
    }
    return config;
  };

  const emitAutoOff = (role: Role): void => {
    const config = getAutoConfig(role);
    config.onAt = Math.max(config.offAt + ROOM_AUTO_OFF_MIN_GAP, Math.min(100, config.onAt));
    config.offAt = Math.max(0, Math.min(100 - ROOM_AUTO_OFF_MIN_GAP, config.offAt));
    emitControl({ action: 'set_room_auto_off', room: role, enabled: config.enabled, offAt: config.offAt, onAt: config.onAt });
  };

  const stopAnimation = (): void => {
    // No animated visual to tear down; retained for the controller lifecycle interface.
  };

  const updateUi = (snapshot: GameSnapshot | null): void => {
    if (!snapshot) {
      return;
    }
    const submarine = snapshot.submarine;
    const isBlacked = submarine.engineOverloaded;

    if (batteryRestoreBtn) {
      batteryRestoreBtn.style.display = isBlacked && submarine.batteryCharge >= 5 && !submarine.powerFailure ? 'block' : 'none';
    }

    if (chargeTargetValue) {
      const serverTarget = Math.round(submarine.batteryChargeTarget ?? 100);
      if (pendingChargeTarget !== null && serverTarget === pendingChargeTarget) {
        pendingChargeTarget = null; // server caught up — resume following snapshots
      }
      chargeTargetValue.textContent = `${pendingChargeTarget ?? serverTarget}%`;
    }

    if (runtimeValue) {
      const netMw = (submarine.powerSupplyMw ?? 0) - (submarine.powerDemandMw ?? 0);
      netMwWindow.push(netMw);
      if (netMwWindow.length > 10) netMwWindow.shift();
      const avgNetMw = netMwWindow.reduce((sum, v) => sum + v, 0) / netMwWindow.length;
      const ticksPerMinute = 60000 / SIMULATION_INTERVAL_MS;
      const pctPerMinute = avgNetMw * BATTERY_CHARGE_RATE_PER_MW_PER_TICK * ticksPerMinute;
      const charge = submarine.batteryCharge ?? 0;
      const target = submarine.batteryChargeTarget ?? 100;

      if (Math.abs(pctPerMinute) < 0.05) {
        runtimeValue.textContent = 'HOLDING';
      } else if (pctPerMinute > 0) {
        const headroom = Math.max(0, target - charge);
        runtimeValue.textContent = headroom < 0.5
          ? 'AT TARGET'
          : `FULL IN ${Math.ceil(headroom / pctPerMinute)} MIN`;
      } else {
        runtimeValue.textContent = `EMPTY IN ${Math.ceil(charge / -pctPerMinute)} MIN`;
      }
    }

    // Keep the room list ordered by current power draw (highest first).
    if (roomListEl) {
      const drawOf = (role: Role): number => submarine.powerBreakdown?.[role] ?? 0;
      const sorted = [...rowElements.keys()].sort((a, b) => drawOf(b) - drawOf(a));
      const current = [...roomListEl.children];
      if (sorted.some((role, i) => rowElements.get(role) !== current[i])) {
        for (const role of sorted) roomListEl.append(rowElements.get(role) as HTMLElement);
      }
    }

    for (const [role, refs] of rowRefs) {
      const mw = submarine.powerBreakdown?.[role] ?? 0;
      const powered = submarine.roomPower?.[role] !== false;

      // Collapsed summary: total room power (or OFF when the room is unpowered).
      refs.mwValue.textContent = powered ? `${mw.toFixed(1)} MW` : 'OFF';
      refs.mwValue.classList.toggle('battery-room-mw-off', !powered);

      // Rooms the server holds dark (upgrade not owned yet, or flooded on purpose) cannot be switched.
      const forcedOff = submarine.powerForcedOff?.includes(role) ?? false;
      refs.powerButton.disabled = forcedOff;
      refs.powerButton.textContent = forcedOff ? (submarine.lockedRoles?.includes(role) ? 'LOCKED' : 'FLOODED') : powered ? 'ON' : 'OFF';
      refs.powerButton.classList.toggle('battery-room-on', powered);
      refs.powerButton.classList.toggle('battery-room-off', !powered);

      const serverAuto = submarine.roomAutoOff?.[role];
      const config = getAutoConfig(role);
      // Keep the local edit state in sync with server-applied enable/disable.
      if (serverAuto && serverAuto.enabled !== config.enabled) {
        config.enabled = serverAuto.enabled;
      }
      refs.autoButton.textContent = config.enabled ? 'Auto-off: ON' : 'Auto-off: OFF';
      refs.autoButton.classList.toggle('is-active', config.enabled);
      refs.thresholdWrap.style.display = config.enabled ? 'grid' : 'none';
      refs.offValue.textContent = `${config.offAt}%`;
      refs.onValue.textContent = `${config.onAt}%`;

      // Rebuild the component breakdown only while the row is expanded.
      if (expandedRoles.has(role)) {
        const components = getRoomComponents(role, submarine);
        refs.components.innerHTML = components.length > 0
          ? components.map((c) => `<span class="battery-room-component"><span class="battery-room-component-label">${c.label}</span><span class="battery-room-component-mw">${c.mw.toFixed(1)} MW</span></span>`).join('')
          : '<span class="battery-room-component battery-room-component-idle">No active draw</span>';
      }
    }
  };

  const buildRoomRow = (role: Role): HTMLElement => {
    const row = document.createElement('div');
    row.className = 'battery-room-row';

    // --- Collapsed header: expand icon + room name + total power (always visible) ---
    const header = document.createElement('button');
    header.type = 'button';
    header.className = 'battery-room-row-header';
    header.setAttribute('aria-expanded', 'false');

    const expandIcon = document.createElement('span');
    expandIcon.className = 'battery-room-expand-icon';
    expandIcon.textContent = '+';

    const name = document.createElement('span');
    name.className = 'battery-room-row-name';
    name.textContent = ROOM_LABELS[role] ?? role;

    const mwValue = document.createElement('span');
    mwValue.className = 'battery-room-row-mw';
    mwValue.textContent = '0.0 MW';

    header.append(expandIcon, name, mwValue);

    // --- Expanded body: component breakdown + on/off + auto-off controls ---
    const body = document.createElement('div');
    body.className = 'battery-room-row-body';
    body.style.display = 'none';

    const components = document.createElement('div');
    components.className = 'battery-room-components';

    const powerButton = document.createElement('button');
    powerButton.type = 'button';
    powerButton.className = 'battery-room-power-toggle';
    powerButton.textContent = 'ON';
    powerButton.addEventListener('click', () => {
      const powered = controllerState.lastSnapshot?.submarine.roomPower?.[role] !== false;
      emitControl({ action: 'set_room_power', room: role, powered: !powered });
    });

    const powerRow = document.createElement('div');
    powerRow.className = 'battery-room-power-row';
    const powerLabel = document.createElement('span');
    powerLabel.className = 'battery-room-power-label';
    powerLabel.textContent = t('room_power');
    powerRow.append(powerLabel, powerButton);

    const autoButton = document.createElement('button');
    autoButton.type = 'button';
    autoButton.className = 'battery-room-auto-toggle secondary';
    autoButton.textContent = 'Auto-off: OFF';
    autoButton.addEventListener('click', () => {
      const config = getAutoConfig(role);
      config.enabled = !config.enabled;
      emitAutoOff(role);
      updateUi(controllerState.lastSnapshot);
    });

    const thresholdWrap = document.createElement('div');
    thresholdWrap.className = 'battery-room-thresholds';
    thresholdWrap.style.display = 'none';

    const buildThreshold = (label: string, key: 'offAt' | 'onAt'): { wrap: HTMLElement; value: HTMLElement } => {
      const wrap = document.createElement('div');
      wrap.className = 'battery-room-threshold';
      const value = document.createElement('div');
      value.className = 'battery-room-threshold-value';
      value.textContent = '0%';
      const control = createPlusMinusControl(
        label,
        { ariaLabel: `Lower ${label}`, onStep: () => stepThreshold(role, key, -5) },
        { ariaLabel: `Raise ${label}`, onStep: () => stepThreshold(role, key, 5) }
      );
      wrap.append(value, control.wrap);
      return { wrap, value };
    };

    const offThreshold = buildThreshold('Off at', 'offAt');
    const onThreshold = buildThreshold('On at', 'onAt');
    thresholdWrap.append(offThreshold.wrap, onThreshold.wrap);

    body.append(components, powerRow, autoButton, thresholdWrap);

    header.addEventListener('click', () => {
      const open = !expandedRoles.has(role);
      if (open) expandedRoles.add(role); else expandedRoles.delete(role);
      header.setAttribute('aria-expanded', open ? 'true' : 'false');
      expandIcon.textContent = open ? '−' : '+';
      body.style.display = open ? 'block' : 'none';
      row.classList.toggle('is-expanded', open);
      updateUi(controllerState.lastSnapshot);
    });

    row.append(header, body);

    rowElements.set(role, row);
    rowRefs.set(role, {
      mwValue,
      expandIcon,
      body,
      components,
      powerButton,
      autoButton,
      thresholdWrap,
      offValue: offThreshold.value,
      onValue: onThreshold.value
    });

    return row;
  };

  const stepThreshold = (role: Role, key: 'offAt' | 'onAt', delta: number): void => {
    const config = getAutoConfig(role);
    config[key] = Math.max(0, Math.min(100, config[key] + delta));
    emitAutoOff(role);
    updateUi(controllerState.lastSnapshot);
  };

  const render = (): void => {
    if (!controls) {
      return;
    }
    rowRefs.clear();
    rowElements.clear();
    controls.classList.add('battery-controls-layout');

    // Battery / demand / net readouts are rendered into the upper-left zone by the
    // controller shell (see controller.ts role-readout block).

    // Charge target + runtime estimate live in the lower-left zone (docs/issues.md:
    // target controls belong in the left sidebar, not the main screen zone).
    const targetWrap = document.createElement('div');
    targetWrap.className = 'battery-target-panel';

    const targetHeading = document.createElement('div');
    targetHeading.className = 'battery-target-heading';
    targetHeading.textContent = 'Charge target';

    const targetValue = document.createElement('div');
    targetValue.className = 'ctrl-seg-display battery-target-value';
    targetValue.textContent = '100%';
    chargeTargetValue = targetValue;

    const stepTarget = (delta: number): void => {
      const current = pendingChargeTarget
        ?? Math.round(controllerState.lastSnapshot?.submarine.batteryChargeTarget ?? 100);
      const next = Math.max(0, Math.min(100, current + delta));
      pendingChargeTarget = next;
      if (chargeTargetValue) chargeTargetValue.textContent = `${next}%`;
      emitControl({ action: 'battery_set_target', value: next });
    };
    const targetControl = createPlusMinusControl(
      'Target',
      { ariaLabel: 'Lower charge target', onStep: () => stepTarget(-5) },
      { ariaLabel: 'Raise charge target', onStep: () => stepTarget(5) }
    );

    const runtimeHeading = document.createElement('div');
    runtimeHeading.className = 'battery-target-heading';
    runtimeHeading.textContent = 'Battery runtime';

    const runtimeDisplay = document.createElement('div');
    runtimeDisplay.className = 'ctrl-seg-display cyan battery-runtime-value';
    runtimeDisplay.textContent = '—';
    runtimeValue = runtimeDisplay;

    targetWrap.append(targetHeading, targetValue, targetControl.wrap, runtimeHeading, runtimeDisplay);
    (ctrlZoneLeftBottom ?? controls).append(targetWrap);

    // Restore-power control lives in the lower-left zone (only shown during blackout).
    const restoreBtn = document.createElement('button');
    restoreBtn.type = 'button';
    restoreBtn.className = 'secondary';
    restoreBtn.textContent = t('restore_power');
    restoreBtn.style.width = '100%';
    restoreBtn.style.display = 'none';
    restoreBtn.addEventListener('click', () => emitControl({ action: 'battery_reset' }));
    batteryRestoreBtn = restoreBtn;
    (ctrlZoneLeftBottom ?? controls).append(restoreBtn);

    // Room power list lives in the middle (main) zone.
    const listCard = createCard('Room Power', 'Cut power to a room or arm automatic battery-level cut-off.');
    listCard.classList.add('ctrl-room-main-card');
    const list = document.createElement('div');
    list.className = 'battery-room-list';
    roomListEl = list;
    for (const role of POWER_CONTROLLABLE_ROLES) {
      list.append(buildRoomRow(role));
    }
    listCard.append(list);

    controls.append(listCard);

    updateUi(controllerState.lastSnapshot);
  };

  return {
    render,
    stopAnimation,
    updateUi
  };
};
